import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { formatApiErrorDetail } from "@/lib/errors";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Card, CardContent } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { toast } from "sonner";
import { Loader2, AlertTriangle, Plus, Trash2 } from "lucide-react";
import { HelpTip } from "@/components/HelpTip";
import { ProductFormats } from "@/components/pricing/ProductFormats";

const codesOf = (data) => Object.keys(data.config.formats || {});
const labelsOf = (data) => Object.fromEntries(codesOf(data).map((c) => [c, data.config.formats[c]?.label || c]));
const LEVELS = ["standard", "preferred", "premier", "strategic"];
const LEVEL_LABEL = { standard: "Standard", preferred: "Preferred", premier: "Premier", strategic: "Strategic" };
const STATUSES = ["STANDARD", "PREFERRED", "PREMIER", "STRATEGIC"];

const money = (n) => (typeof n === "number" && Number.isFinite(n) ? `$${n.toFixed(2)}` : "—");
const pct = (n) => (typeof n === "number" && Number.isFinite(n) ? `${n.toFixed(1)}%` : "—");

function WarningBanner({ warnings, labels }) {
  if (!warnings?.length) return null;
  const FMT_LABEL = labels;
  return (
    <Card className="border-status-sold/40 bg-[#D4A373]/10" data-testid="pricing-warnings">
      <CardContent className="p-4">
        {warnings.map((w, i) => (
          <p key={i} className="flex items-center gap-2 text-sm text-[#8a6435]" data-testid={`pricing-warning-${i}`}>
            <AlertTriangle className="h-4 w-4" />
            {w.kind === "below_container_floor"
              ? `${FMT_LABEL[w.format]} container price ${money(w.price)} is below the internal floor ${money(w.floor)}`
              : `${FMT_LABEL[w.format]} ${w.level || "container"} price ${money(w.price)} → margin ${pct(w.margin_pct)} below target ${pct(w.target_margin_pct)}`}
          </p>
        ))}
      </CardContent>
    </Card>
  );
}

function SaveRow({ onSave, saving, testid }) {
  return (
    <div className="mt-4 flex justify-end">
      <Button onClick={onSave} disabled={saving} data-testid={testid} className="bg-terracotta text-white hover:bg-terracotta-hover">
        {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save changes"}
      </Button>
    </div>
  );
}

function FormatPricing({ data, save, saving }) {
  const FORMATS = codesOf(data);
  const FMT_LABEL = labelsOf(data);
  const [prices, setPrices] = useState(data.config.format_prices);
  const [costs, setCosts] = useState(data.config.costs);
  const [target, setTarget] = useState(String(data.config.target_margin_pct));
  useEffect(() => {
    setPrices(data.config.format_prices);
    setCosts(data.config.costs);
    setTarget(String(data.config.target_margin_pct));
  }, [data]);
  const setPrice = (fmt, level) => (e) =>
    setPrices((p) => ({ ...p, [fmt]: { ...p[fmt], [level]: e.target.value } }));
  const setCost = (fmt, field) => (e) =>
    setCosts((c) => ({ ...c, [fmt]: { ...c[fmt], [field]: e.target.value } }));

  // Live margin math — GP = price − landed cost, GM = GP / price × 100, the same
  // formula as the backend (landed cost = supplier cost + inbound freight allocation).
  // Computed from the EDITED input values so GM/GP recalculate while typing.
  const landedFor = (fmt) => {
    const sc = parseFloat(costs[fmt]?.supplier_cost);
    const inb = parseFloat(costs[fmt]?.inbound_freight_alloc);
    if (!Number.isFinite(sc) && !Number.isFinite(inb)) return null;
    return (Number.isFinite(sc) ? sc : 0) + (Number.isFinite(inb) ? inb : 0);
  };
  const targetNum = parseFloat(target);
  const threshold = Number.isFinite(targetNum) ? targetNum : 25;
  const marginFor = (priceRaw, cost) => {
    const price = parseFloat(priceRaw);
    if (!Number.isFinite(price) || price <= 0 || cost == null) return null;  // blank/zero price → no invented margin
    const gp = price - cost;
    return { gp, gm: (gp / price) * 100 };
  };
  const MarginNote = ({ m, testid }) => (
    <p className={`mt-1 text-[10px] ${m && m.gm < threshold ? "font-medium text-terracotta" : "text-muted-foreground"}`} data-testid={testid}>
      {m ? `GM ${m.gm.toFixed(1)}% · GP $${m.gp.toFixed(2)}` : "GM — · GP —"}
      {m && m.gm < threshold && <span className="block">Below {threshold}% target</span>}
    </p>
  );

  const saveAll = async () => {
    // ID-060: formats/designs left fully blank stay unpriced (never sent as $0); partial tiers are rejected
    const nums = (f) => LEVELS.map((l) => parseFloat(prices[f]?.[l]));
    const partial = FORMATS.filter((f) => { const n = nums(f).filter(Number.isFinite).length; return n > 0 && n < LEVELS.length; });
    if (partial.length) { toast.error(`Enter all four tier prices for ${partial.map((f) => FMT_LABEL[f]).join(", ")} or leave them all blank`); return; }
    const cleanPrices = Object.fromEntries(
      FORMATS.filter((f) => nums(f).every(Number.isFinite)).map((f) => [f, Object.fromEntries(LEVELS.map((l) => [l, parseFloat(prices[f][l])]))])
    );
    const cleanCosts = Object.fromEntries(
      FORMATS.filter((f) => Number.isFinite(parseFloat(costs[f]?.supplier_cost)))
        .map((f) => [f, { supplier_cost: parseFloat(costs[f].supplier_cost), inbound_freight_alloc: parseFloat(costs[f]?.inbound_freight_alloc) || 0 }])
    );
    await save("format_prices", cleanPrices);
    await save("costs", cleanCosts);
    await save("target_margin_pct", parseFloat(target));
  };

  return (
    <div data-testid="tab-content-format-pricing">
      <div className="overflow-x-auto rounded-md border border-border bg-white">
        <Table className="min-w-[860px]" data-testid="format-prices-table">
          <TableHeader>
            <TableRow className="border-border">
              <TableHead className="text-xs uppercase tracking-widest text-muted-foreground">Format</TableHead>
              {LEVELS.map((l) => (
                <TableHead key={l} className="text-xs uppercase tracking-widest text-muted-foreground">{LEVEL_LABEL[l]} $/box</TableHead>
              ))}
            </TableRow>
          </TableHeader>
          <TableBody>
            {FORMATS.map((fmt) => (
              <TableRow key={fmt} className="border-border" data-testid={`price-row-${fmt}`}>
                <TableCell className="py-3 text-sm font-medium text-charcoal">
                  {FMT_LABEL[fmt]}
                  <p className="mt-0.5 text-[10px] font-normal text-muted-foreground" data-testid={`landed-cost-${fmt}`}>
                    Landed cost: {landedFor(fmt) != null ? `$${landedFor(fmt).toFixed(2)}/box` : "—"}
                  </p>
                </TableCell>
                {LEVELS.map((level) => (
                  <TableCell key={level} className="py-3">
                    <Input type="number" step="0.01" min="0" value={prices[fmt]?.[level] ?? ""} onChange={setPrice(fmt, level)}
                      data-testid={`price-input-${fmt}-${level}`} className="h-9 w-24 bg-white focus-visible:ring-terracotta" />
                    <MarginNote m={marginFor(prices[fmt]?.[level], landedFor(fmt))} testid={`gm-gp-${fmt}-${level}`} />
                  </TableCell>
                ))}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      <div className="mt-6 overflow-x-auto rounded-md border border-border bg-white">
        <Table className="min-w-[700px]" data-testid="costs-table">
          <TableHeader>
            <TableRow className="border-border">
              <TableHead className="text-xs uppercase tracking-widest text-muted-foreground">Format</TableHead>
              <TableHead className="text-xs uppercase tracking-widest text-muted-foreground">Supplier cost $/box</TableHead>
              <TableHead className="text-xs uppercase tracking-widest text-muted-foreground">Inbound alloc $/box</TableHead>
              <TableHead className="text-xs uppercase tracking-widest text-muted-foreground">Landed cost</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {FORMATS.map((fmt) => (
              <TableRow key={fmt} className="border-border" data-testid={`cost-row-${fmt}`}>
                <TableCell className="py-3 text-sm font-medium text-charcoal">{FMT_LABEL[fmt]}</TableCell>
                <TableCell className="py-3"><Input type="number" step="0.01" min="0" value={costs[fmt]?.supplier_cost ?? ""} onChange={setCost(fmt, "supplier_cost")} data-testid={`cost-input-${fmt}`} className="h-9 w-24 bg-white focus-visible:ring-terracotta" /></TableCell>
                <TableCell className="py-3"><Input type="number" step="0.01" min="0" value={costs[fmt]?.inbound_freight_alloc ?? ""} onChange={setCost(fmt, "inbound_freight_alloc")} data-testid={`inbound-input-${fmt}`} className="h-9 w-24 bg-white focus-visible:ring-terracotta" /></TableCell>
                <TableCell className="py-3 text-sm text-warmgrey" data-testid={`costs-landed-${fmt}`}>{landedFor(fmt) != null ? money(landedFor(fmt)) : "—"}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      <div className="mt-4 flex max-w-xs items-end gap-3">
        <div className="flex-1 space-y-2">
          <Label className="text-xs font-semibold uppercase tracking-wider text-warmgrey">Target gross margin %</Label>
          <Input type="number" step="0.1" min="0" max="95" value={target} onChange={(e) => setTarget(e.target.value)} data-testid="target-margin-input" className="h-11 bg-white focus-visible:ring-terracotta" />
        </div>
      </div>
      <p className="mt-2 text-xs text-muted-foreground">Private cost data — never shown to dealers. Margin = (price − landed cost) / price.</p>
      <SaveRow onSave={saveAll} saving={saving} testid="save-format-pricing" />
    </div>
  );
}

function VolumeStatus({ data, save, saving }) {
  const [thresholds, setThresholds] = useState(data.config.volume_thresholds || []);
  const [statusT, setStatusT] = useState(data.config.status_thresholds_quarterly_sqft || {});
  const [grace, setGrace] = useState(String(data.config.status_grace_days ?? 30));
  const [map, setMap] = useState(data.config.status_price_level_map || {});
  useEffect(() => {
    setThresholds(data.config.volume_thresholds || []);
    setStatusT(data.config.status_thresholds_quarterly_sqft || {});
    setGrace(String(data.config.status_grace_days ?? 30));
    setMap(data.config.status_price_level_map || {});
  }, [data]);

  const saveAll = async () => {
    await save("volume_thresholds", thresholds
      .filter((t) => t.min_sqft !== "" && t.level)
      .map((t) => ({ min_sqft: parseFloat(t.min_sqft), level: t.level })));
    await save("status_thresholds_quarterly_sqft",
      Object.fromEntries(Object.entries(statusT).map(([k, v]) => [k, parseFloat(v)])));
    await save("status_grace_days", parseInt(grace, 10));
    await save("status_price_level_map", map);
  };

  return (
    <div data-testid="tab-content-volume-status" className="space-y-8">
      <div>
        <p className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-[0.2em] text-charcoal">
          Order-volume qualification
          <HelpTip testid="help-order-volume" text="Order-volume qualification is earned by the size of THIS order alone — enough eligible square footage in a single order unlocks a better price level immediately, regardless of the dealer's status." />
        </p>
        <p className="mt-1 text-xs text-muted-foreground">Total eligible sqft across the complete order (all formats combined) unlocks that price level on every line.</p>
        <div className="mt-3 space-y-2">
          {thresholds.map((t, i) => (
            <div key={i} className="flex items-center gap-3" data-testid={`volume-threshold-${i}`}>
              <Input type="number" min="0" value={t.min_sqft} onChange={(e) => setThresholds((rows) => rows.map((r, j) => (j === i ? { ...r, min_sqft: e.target.value } : r)))} data-testid={`volume-min-${i}`} className="h-10 w-32 bg-white focus-visible:ring-terracotta" />
              <span className="text-xs text-muted-foreground">eligible sqft →</span>
              <Select value={t.level} onValueChange={(v) => setThresholds((rows) => rows.map((r, j) => (j === i ? { ...r, level: v } : r)))}>
                <SelectTrigger data-testid={`volume-level-${i}`} className="h-10 w-40 bg-white"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-white">{LEVELS.filter((l) => l !== "standard").map((l) => <SelectItem key={l} value={l}>{LEVEL_LABEL[l]}</SelectItem>)}</SelectContent>
              </Select>
              <Button variant="ghost" size="icon" onClick={() => setThresholds((rows) => rows.filter((_, j) => j !== i))} data-testid={`volume-remove-${i}`}><Trash2 className="h-4 w-4 text-status-sold" /></Button>
            </div>
          ))}
          <Button variant="outline" size="sm" onClick={() => setThresholds((rows) => [...rows, { min_sqft: "", level: "preferred" }])} data-testid="volume-add" className="border-limestone text-charcoal hover:bg-sand">
            <Plus className="mr-1 h-3.5 w-3.5" /> Add threshold
          </Button>
        </div>
      </div>

      <div>
        <p className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-[0.2em] text-charcoal">
          Dealer status — quarterly qualification
          <HelpTip testid="help-dealer-status" text="Dealer status is the earned relationship tier, based on cumulative quarterly purchasing. It applies to every order the dealer places until the next review — even small ones." />
        </p>
        <p className="mt-1 text-xs text-muted-foreground">Cumulative eligible sqft in a quarter. V1 recommends only — status changes are always an explicit admin action.</p>
        <div className="mt-3 grid grid-cols-2 gap-3 sm:grid-cols-4">
          {STATUSES.slice(1).map((st) => (
            <div key={st} className="space-y-1">
              <Label className="text-xs font-semibold uppercase tracking-wider text-warmgrey">{st}</Label>
              <Input type="number" min="0" value={statusT[st] ?? ""} onChange={(e) => setStatusT((s) => ({ ...s, [st]: e.target.value }))} data-testid={`status-threshold-${st.toLowerCase()}`} className="h-10 bg-white focus-visible:ring-terracotta" />
            </div>
          ))}
          <div className="space-y-1">
            <Label className="text-xs font-semibold uppercase tracking-wider text-warmgrey">Grace days</Label>
            <Input type="number" min="0" value={grace} onChange={(e) => setGrace(e.target.value)} data-testid="grace-days-input" className="h-10 bg-white focus-visible:ring-terracotta" />
          </div>
        </div>
      </div>

      <div>
        <p className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-[0.2em] text-charcoal">
          Dealer status → pricing level
          <HelpTip testid="help-status-vs-volume" text="Dealer status is the earned relationship tier; order-volume qualification is earned independently by the size of the current order. For every order the engine calculates both and applies the better (lower) eligible price." />
        </p>
        <p className="mt-1 max-w-2xl text-xs leading-relaxed text-muted-foreground">
          Each dealer status earns a standing pricing level — e.g. a PREFERRED dealer receives the preferred price level on every order, even small ones. When a single order is large enough to qualify for a better level by size, the better eligible price wins.
        </p>
        <div className="mt-3 grid grid-cols-2 gap-3 sm:grid-cols-4">
          {STATUSES.map((st) => (
            <div key={st} className="space-y-1">
              <Label className="text-xs font-semibold uppercase tracking-wider text-warmgrey">{st}</Label>
              <Select value={map[st]} onValueChange={(v) => setMap((m) => ({ ...m, [st]: v }))}>
                <SelectTrigger data-testid={`status-map-${st.toLowerCase()}`} className="h-10 bg-white"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-white">{LEVELS.map((l) => <SelectItem key={l} value={l}>{LEVEL_LABEL[l]}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          ))}
        </div>
      </div>
      <SaveRow onSave={saveAll} saving={saving} testid="save-volume-status" />
    </div>
  );
}

function Premiums({ premiums, reload, labels }) {
  const FMT_LABEL = labels;
  const [form, setForm] = useState({ code: "", name: "", amount_per_box: "" });
  const saveAmount = async (p, value) => {
    try {
      await api.patch(`/admin/pricing/premiums/${p.id}`, { amount_per_box: parseFloat(value) });
      toast.success(`${p.code} premium updated`);
      reload();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const toggle = async (p, field, value) => {
    try {
      await api.patch(`/admin/pricing/premiums/${p.id}`, { [field]: value });
      reload();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const create = async (e) => {
    e.preventDefault();
    try {
      await api.post("/admin/pricing/premiums", { code: form.code, name: form.name, amount_per_box: parseFloat(form.amount_per_box) });
      toast.success("Premium created");
      setForm({ code: "", name: "", amount_per_box: "" });
      reload();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };
  return (
    <div data-testid="tab-content-premiums" className="space-y-4">
      <div className="overflow-x-auto rounded-md border border-border bg-white">
        <Table className="min-w-[720px]" data-testid="premiums-table">
          <TableHeader>
            <TableRow className="border-border">
              <TableHead className="text-xs uppercase tracking-widest text-muted-foreground">Color code</TableHead>
              <TableHead className="text-xs uppercase tracking-widest text-muted-foreground">Name</TableHead>
              <TableHead className="text-xs uppercase tracking-widest text-muted-foreground">+$/box</TableHead>
              <TableHead className="text-xs uppercase tracking-widest text-muted-foreground">Formats</TableHead>
              <TableHead className="text-xs uppercase tracking-widest text-muted-foreground">Container too</TableHead>
              <TableHead className="text-xs uppercase tracking-widest text-muted-foreground">Active</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {premiums.map((p) => (
              <TableRow key={p.id} className="border-border" data-testid={`premium-row-${p.code}`}>
                <TableCell className="py-3 font-mono text-xs text-charcoal">{p.code}</TableCell>
                <TableCell className="py-3 text-sm text-charcoal">{p.name}</TableCell>
                <TableCell className="py-3">
                  <Input type="number" step="0.01" min="0" defaultValue={p.amount_per_box} onBlur={(e) => parseFloat(e.target.value) !== p.amount_per_box && saveAmount(p, e.target.value)} data-testid={`premium-amount-${p.code}`} className="h-9 w-24 bg-white focus-visible:ring-terracotta" />
                </TableCell>
                <TableCell className="py-3 text-xs text-warmgrey" data-testid={`premium-formats-${p.code}`}>
                  <div className="flex flex-wrap gap-1">
                    {Object.keys(labels || {}).map((f) => {
                      const on = (p.formats || []).includes(f);
                      return (
                        <button key={f} type="button" data-testid={`premium-format-${p.code}-${f}`} title={on ? "Click to remove this format" : "Click to add this format"}
                          onClick={() => toggle(p, "formats", on ? p.formats.filter((x) => x !== f) : [...(p.formats || []), f])}
                          className={`rounded-full border px-2 py-0.5 text-[11px] ${on ? "border-terracotta bg-terracotta/10 text-terracotta" : "border-border text-warmgrey hover:border-charcoal"}`}>
                          {on ? "✓ " : "+ "}{labels[f] || FMT_LABEL[f] || f}
                        </button>
                      );
                    })}
                  </div>
                </TableCell>
                <TableCell className="py-3"><Switch checked={p.applies_to_container} onCheckedChange={(v) => toggle(p, "applies_to_container", v)} data-testid={`premium-container-${p.code}`} /></TableCell>
                <TableCell className="py-3"><Switch checked={p.active} onCheckedChange={(v) => toggle(p, "active", v)} data-testid={`premium-active-${p.code}`} /></TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
      <form onSubmit={create} className="flex flex-wrap items-end gap-3" data-testid="premium-create-form">
        <div className="space-y-1"><Label className="text-xs font-semibold uppercase tracking-wider text-warmgrey">Code</Label><Input required value={form.code} onChange={(e) => setForm((f) => ({ ...f, code: e.target.value }))} data-testid="premium-code-input" placeholder="MZ004" className="h-10 w-28 bg-white focus-visible:ring-terracotta" /></div>
        <div className="space-y-1"><Label className="text-xs font-semibold uppercase tracking-wider text-warmgrey">Name</Label><Input value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} data-testid="premium-name-input" placeholder="BYED FAIENCE" className="h-10 w-48 bg-white focus-visible:ring-terracotta" /></div>
        <div className="space-y-1"><Label className="text-xs font-semibold uppercase tracking-wider text-warmgrey">+$/box</Label><Input required type="number" step="0.01" min="0" value={form.amount_per_box} onChange={(e) => setForm((f) => ({ ...f, amount_per_box: e.target.value }))} data-testid="premium-amount-input" className="h-10 w-28 bg-white focus-visible:ring-terracotta" /></div>
        <Button type="submit" data-testid="premium-create-submit" className="h-10 bg-terracotta text-white hover:bg-terracotta-hover"><Plus className="mr-1 h-4 w-4" /> Add premium</Button>
      </form>
    </div>
  );
}

function ContainerSettings({ data, save, saving }) {
  const FORMATS = codesOf(data);
  const FMT_LABEL = labelsOf(data);
  const [c, setC] = useState(data.config.container);
  useEffect(() => setC(data.config.container), [data]);
  // Live container GM — same cost basis as the backend container margin: supplier cost
  // (freight is quoted separately in container mode, never inside box pricing).
  const supplierFor = (fmt) => {
    const v = parseFloat(data.config.costs?.[fmt]?.supplier_cost);
    return Number.isFinite(v) ? v : null;
  };
  const targetParsed = parseFloat(data.config.target_margin_pct);
  const containerThreshold = Number.isFinite(targetParsed) ? targetParsed : 25;
  const setField = (k) => (e) => setC((s) => ({ ...s, [k]: e.target.value }));
  const setMap = (k, fmt) => (e) => setC((s) => ({ ...s, [k]: { ...s[k], [fmt]: e.target.value } }));
  const saveAll = () =>
    save("container", {
      ...c,
      pallet_positions_required: parseInt(c.pallet_positions_required, 10),
      freight_estimate_usd: parseFloat(c.freight_estimate_usd),
      boxes_per_pallet: Object.fromEntries(FORMATS.map((f) => [f, parseInt(c.boxes_per_pallet?.[f] ?? 0, 10)])),
      container_prices: Object.fromEntries(FORMATS.map((f) => [f, parseFloat(c.container_prices?.[f] ?? 0)])),
      container_floors: Object.fromEntries(FORMATS.map((f) => [f, parseFloat(c.container_floors?.[f] ?? 0)])),
    });
  return (
    <div data-testid="tab-content-container" className="space-y-6">
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        <div className="space-y-1">
          <Label className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wider text-warmgrey">
            Pallet positions to qualify
            <HelpTip testid="help-pallet-positions" text="Minimum total pallet positions required for full-container pricing. Formats may be mixed — 4×4, 2×6 and 2×2 can all contribute toward the same qualifying container." />
          </Label>
          <Input type="number" min="1" value={c.pallet_positions_required ?? ""} onChange={setField("pallet_positions_required")} data-testid="container-positions-input" className="h-10 bg-white focus-visible:ring-terracotta" />
        </div>
        <div className="space-y-1">
          <Label className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wider text-warmgrey">
            Freight estimate $/container
            <HelpTip testid="help-freight-estimate" text="Working freight/logistics estimate for a full container. Used for quoting and planning only. Update this value as freight costs change. Freight is separate from product price." />
          </Label>
          <Input type="number" step="0.01" min="0" value={c.freight_estimate_usd ?? ""} onChange={setField("freight_estimate_usd")} data-testid="container-freight-input" className="h-10 bg-white focus-visible:ring-terracotta" />
        </div>
      </div>
      <div className="overflow-x-auto rounded-md border border-border bg-white">
        <Table className="min-w-[720px]" data-testid="container-prices-table">
          <TableHeader>
            <TableRow className="border-border">
              <TableHead className="text-xs uppercase tracking-widest text-muted-foreground">Format</TableHead>
              <TableHead className="text-xs uppercase tracking-widest text-muted-foreground">
                <span className="inline-flex items-center gap-1.5">Container price $/box
                  <HelpTip testid="help-container-price" text="The standard product price charged per box when an order qualifies for full-container pricing. Freight is quoted separately. This price may be set above the Internal Floor." />
                </span>
              </TableHead>
              <TableHead className="text-xs uppercase tracking-widest text-muted-foreground">
                <span className="inline-flex items-center gap-1.5">Internal floor $/box
                  <HelpTip testid="help-internal-floor" text="Your private minimum acceptable product price for this format. Dealers never see this value. Container pricing may be set above the floor. Use the floor as an internal guardrail when approving special pricing." />
                </span>
              </TableHead>
              <TableHead className="text-xs uppercase tracking-widest text-muted-foreground">Boxes / pallet</TableHead>
              <TableHead className="text-xs uppercase tracking-widest text-muted-foreground">Container GM</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {FORMATS.map((fmt) => (
              <TableRow key={fmt} className="border-border" data-testid={`container-row-${fmt}`}>
                <TableCell className="py-3 text-sm font-medium text-charcoal">{FMT_LABEL[fmt]}</TableCell>
                <TableCell className="py-3"><Input type="number" step="0.01" min="0" value={c.container_prices?.[fmt] ?? ""} onChange={setMap("container_prices", fmt)} data-testid={`container-price-${fmt}`} className="h-9 w-24 bg-white focus-visible:ring-terracotta" /></TableCell>
                <TableCell className="py-3"><Input type="number" step="0.01" min="0" value={c.container_floors?.[fmt] ?? ""} onChange={setMap("container_floors", fmt)} data-testid={`container-floor-${fmt}`} className="h-9 w-24 bg-white focus-visible:ring-terracotta" /></TableCell>
                <TableCell className="py-3"><Input type="number" min="1" value={c.boxes_per_pallet?.[fmt] ?? ""} onChange={setMap("boxes_per_pallet", fmt)} data-testid={`container-bpp-${fmt}`} className="h-9 w-24 bg-white focus-visible:ring-terracotta" /></TableCell>
                <TableCell className="py-3 text-sm" data-testid={`container-gm-${fmt}`}>
                  {(() => {
                    const price = parseFloat(c.container_prices?.[fmt]);
                    const cost = supplierFor(fmt);
                    if (!Number.isFinite(price) || price <= 0 || cost == null) return <span className="text-warmgrey">—</span>;
                    const gm = ((price - cost) / price) * 100;
                    return (
                      <span className={gm < containerThreshold ? "font-medium text-terracotta" : "text-warmgrey"}>
                        {gm.toFixed(1)}%{gm < containerThreshold && <span className="block text-[10px]">Below {containerThreshold}% target</span>}
                      </span>
                    );
                  })()}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
      <p className="text-xs text-muted-foreground">Floors are internal and never shown to dealers. Freight is an estimate quoted separately — never inside box pricing.</p>
      <SaveRow onSave={saveAll} saving={saving} testid="save-container" />
    </div>
  );
}

function AuditLog({ events }) {
  return (
    <div data-testid="tab-content-audit">
      <p className="mb-3 flex items-center gap-1.5 text-xs text-muted-foreground">
        Audited
        <HelpTip testid="help-audit-trail" text="Important changes are recorded with who changed what and when, so commercial history can always be reconstructed." />
        — every pricing change below shows the previous value, the new value, the actor and the timestamp.
      </p>
    <div className="overflow-x-auto rounded-md border border-border bg-white">
      <Table className="min-w-[760px]" data-testid="pricing-events-table">
        <TableHeader>
          <TableRow className="border-border">
            <TableHead className="text-xs uppercase tracking-widest text-muted-foreground">When</TableHead>
            <TableHead className="text-xs uppercase tracking-widest text-muted-foreground">Type</TableHead>
            <TableHead className="text-xs uppercase tracking-widest text-muted-foreground">Key</TableHead>
            <TableHead className="text-xs uppercase tracking-widest text-muted-foreground">Change</TableHead>
            <TableHead className="text-xs uppercase tracking-widest text-muted-foreground">Note</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {events.length === 0 ? (
            <TableRow><TableCell colSpan={5} className="py-12 text-center text-sm text-muted-foreground">No pricing events yet.</TableCell></TableRow>
          ) : events.map((e, i) => (
            <TableRow key={e.id || i} className="border-border" data-testid={`pricing-event-${i}`}>
              <TableCell className="py-3 text-xs text-muted-foreground">{new Date(e.at).toLocaleString()}</TableCell>
              <TableCell className="py-3 text-xs font-medium text-charcoal">{e.type}</TableCell>
              <TableCell className="py-3 font-mono text-xs text-warmgrey">{e.key}</TableCell>
              <TableCell className="max-w-[280px] truncate py-3 font-mono text-[11px] text-warmgrey">{JSON.stringify(e.old_value)} → {JSON.stringify(e.new_value)}</TableCell>
              <TableCell className="max-w-[200px] truncate py-3 text-xs text-muted-foreground">{e.note}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
    </div>
  );
}

export default function AdminPricing() {
  const [data, setData] = useState(null);
  const [premiums, setPremiums] = useState([]);
  const [events, setEvents] = useState([]);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    try {
      const [cfg, prem, ev] = await Promise.all([
        api.get("/admin/pricing/config"),
        api.get("/admin/pricing/premiums"),
        api.get("/admin/pricing/events?limit=100"),
      ]);
      setData(cfg.data);
      setPremiums(prem.data);
      setEvents(ev.data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };
  useEffect(() => { load(); }, []);

  const save = async (section, value) => {
    setSaving(true);
    try {
      const { data: next } = await api.put("/admin/pricing/config", { section, value, note: "Admin pricing UI" });
      setData(next);
      const ev = await api.get("/admin/pricing/events?limit=100");
      setEvents(ev.data);
      toast.success("Pricing configuration saved");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  if (!data) {
    return <div className="flex min-h-[40vh] items-center justify-center" data-testid="admin-pricing-loading"><Loader2 className="h-6 w-6 animate-spin text-terracotta" /></div>;
  }

  return (
    <div data-testid="admin-pricing-page">
      <p className="text-xs font-semibold uppercase tracking-[0.2em] text-terracotta">Commercial</p>
      <h1 className="mt-3 font-display text-4xl font-light tracking-tight text-charcoal md:text-5xl">Pricing engine</h1>
      <p className="mt-3 max-w-2xl text-sm leading-relaxed text-warmgrey">
        All commercial values are configuration — changes apply to future orders only and are versioned + audited. Historical orders keep their frozen prices.
      </p>

      <div className="mt-6"><WarningBanner warnings={data.warnings} labels={labelsOf(data)} /></div>

      <Tabs defaultValue="formats" className="mt-8">
        <TabsList className="bg-sand">
          <TabsTrigger value="formats" data-testid="pricing-tab-formats">Format pricing</TabsTrigger>
          <TabsTrigger value="volume" data-testid="pricing-tab-volume">Levels & status</TabsTrigger>
          <TabsTrigger value="premiums" data-testid="pricing-tab-premiums">Premiums</TabsTrigger>
          <TabsTrigger value="container" data-testid="pricing-tab-container">Container</TabsTrigger>
          <TabsTrigger value="audit" data-testid="pricing-tab-audit">Audit log</TabsTrigger>
        </TabsList>
        <TabsContent value="formats" className="mt-6"><ProductFormats onChanged={load} /><FormatPricing data={data} save={save} saving={saving} /></TabsContent>
        <TabsContent value="volume" className="mt-6"><VolumeStatus data={data} save={save} saving={saving} /></TabsContent>
        <TabsContent value="premiums" className="mt-6"><Premiums premiums={premiums} reload={load} labels={labelsOf(data)} /></TabsContent>
        <TabsContent value="container" className="mt-6"><ContainerSettings data={data} save={save} saving={saving} /></TabsContent>
        <TabsContent value="audit" className="mt-6"><AuditLog events={events} /></TabsContent>
      </Tabs>
    </div>
  );
}
